import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { QueryDocumentSnapshot, DocumentData, Timestamp } from 'firebase/firestore';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { AdminService, type AdminSubscription } from '../../services/adminService';
import { invoiceService } from '../../services/invoiceService';

const ADMIN_BASE = '/admin/control-panel-7x9k2m';

function formatDate(ts: Timestamp | undefined): string {
  if (!ts?.toDate) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(ts.toDate());
}

function formatMoneyMinorUnits(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: (currency || 'USD').toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format((cents || 0) / 100);
}

function truncateId(id: string | undefined, max = 14): string {
  if (!id) return '—';
  if (id.length <= max) return id;
  return `${id.slice(0, max)}…`;
}

function providerLabel(row: AdminSubscription): string {
  const p = (row.paymentProvider || '').toLowerCase();
  if (p) return p;
  const sid = row.stripeSubscriptionId || '';
  if (sid.startsWith('dodo_')) return 'dodo';
  if (row.dodoSubscriptionId) return 'dodo';
  const ref = row.lastPaymentReference || '';
  if (ref.startsWith('pay_')) return 'razorpay';
  if (ref.startsWith('pi_')) return 'stripe';
  if (row.stripeSubscriptionId) return 'stripe';
  return '—';
}

function externalSubscriptionId(row: AdminSubscription): string {
  return (
    row.dodoSubscriptionId ||
    row.dodoPaymentId ||
    row.lastPaymentReference ||
    row.stripeSubscriptionId ||
    ''
  );
}

/** Fill missing gateway / payment id from latest `invoices` row (e.g. Dodo `subscriptionId` `dodo_…`, Razorpay `pay_…`). */
function mergeSubscriptionPaymentHints(
  subscriptions: AdminSubscription[],
  hints: Record<string, { provider: string; externalId: string }>
): AdminSubscription[] {
  return subscriptions.map((sub) => {
    const h = hints[sub.userId];
    if (!h || h.provider === 'unknown') return sub;
    const ext = h.externalId ? String(h.externalId) : '';

    if (sub.paymentProvider) {
      if (!sub.lastPaymentReference && ext && !ext.startsWith('dodo_')) {
        return { ...sub, lastPaymentReference: ext };
      }
      return sub;
    }

    const out: AdminSubscription = {
      ...sub,
      paymentProvider: h.provider,
      lastPaymentReference:
        sub.lastPaymentReference || (ext && !ext.startsWith('dodo_') ? ext : sub.lastPaymentReference)
    };
    // Dodo checkout writes `stripeSubscriptionId: dodo_<paymentId>` on the user; if that was wiped, restore from invoice.
    if (h.provider === 'dodo' && ext.startsWith('dodo_') && !out.stripeSubscriptionId) {
      out.stripeSubscriptionId = ext;
    }
    return out;
  });
}

function mergeInvoiceActions(
  subscriptions: AdminSubscription[],
  invoiceLinks: Record<string, { invoiceId: string; viewUrl: string; downloadUrl: string }>
): AdminSubscription[] {
  return subscriptions.map((sub) => {
    const inv = invoiceLinks[sub.userId];
    if (!inv) return sub;
    return {
      ...sub,
      latestInvoiceId: inv.invoiceId || sub.latestInvoiceId,
      latestInvoiceViewUrl: inv.viewUrl || sub.latestInvoiceViewUrl,
      latestInvoiceDownloadUrl: inv.downloadUrl || sub.latestInvoiceDownloadUrl
    };
  });
}

function mergeInvoiceAmounts(
  subscriptions: AdminSubscription[],
  amounts: Record<string, { amountPaidMinor: number; currency: string; created: number }>
): AdminSubscription[] {
  return subscriptions.map((sub) => {
    const a = amounts[sub.userId];
    if (!a) return sub;
    return {
      ...sub,
      // Prefer real paid invoice amount for MRR + invoice generation.
      amount: Number.isFinite(Number(a.amountPaidMinor)) ? Math.round(Number(a.amountPaidMinor)) : sub.amount,
      currency: String(a.currency || sub.currency || 'usd').toUpperCase(),
      latestInvoiceAmountPaidMinor: a.amountPaidMinor,
      latestInvoiceCurrency: a.currency,
      latestInvoiceCreated: a.created,
    };
  });
}

const UNLIMITED_EMAILS_SENTINEL = 1_000_000_000;

/** Human-readable product line (Pro / Business / Enterprise). */
function planTypeLabel(planId: string | undefined): string {
  const p = (planId || '').toLowerCase().trim();
  if (p === 'business') return 'Business';
  if (p === 'pro') return 'Pro';
  if (p === 'enterprise') return 'Enterprise';
  return planId ? planId.charAt(0).toUpperCase() + planId.slice(1).toLowerCase() : '—';
}

function planTypeBadgeClass(planId: string | undefined, isDark: boolean): string {
  const p = (planId || '').toLowerCase();
  if (p === 'business') {
    return isDark ? 'bg-violet-900/50 text-violet-100' : 'bg-violet-100 text-violet-900';
  }
  if (p === 'enterprise') {
    return isDark ? 'bg-amber-900/40 text-amber-100' : 'bg-amber-100 text-amber-900';
  }
  return isDark ? 'bg-sky-900/40 text-sky-100' : 'bg-sky-100 text-sky-900';
}

function formatEmailsLine(emails: number | undefined): string | null {
  if (emails == null) return null;
  if (emails >= UNLIMITED_EMAILS_SENTINEL / 2) return 'Unlimited emails/mo';
  return `${emails.toLocaleString()} emails/mo`;
}

const AdminCurrentSubscriptionsPage: React.FC = () => {
  const { isDark } = useAdminTheme();
  const { showError, showSuccess } = useNotification();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [rows, setRows] = useState<AdminSubscription[]>([]);
  const [cursor, setCursor] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [generatingInvoiceForUserId, setGeneratingInvoiceForUserId] = useState<string | null>(null);

  const sortRows = useCallback((list: AdminSubscription[]) => {
    return [...list].sort((a, b) => {
      const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
      const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
      return tb - ta;
    });
  }, []);

  const loadFirst = useCallback(async () => {
    try {
      setLoading(true);
      const [page, hints, invoiceLinks, invoiceAmounts] = await Promise.all([
        AdminService.getSubscriptionsPage(100, null),
        AdminService.getLatestPaymentGatewayHintsByUserId(500),
        AdminService.getLatestInvoiceLinksByUserId(1000),
        AdminService.getLatestPaidInvoiceAmountsByUserId(1500)
      ]);
      const merged = mergeInvoiceActions(mergeSubscriptionPaymentHints(page.subscriptions, hints), invoiceLinks);
      setRows(sortRows(mergeInvoiceAmounts(merged, invoiceAmounts)));
      setCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch (e) {
      console.error('AdminCurrentSubscriptionsPage', e);
      showError('Could not load subscriptions. Deploy the Firestore composite index for users if this is the first run.');
    } finally {
      setLoading(false);
    }
  }, [showError, sortRows]);

  useEffect(() => {
    loadFirst();
  }, [loadFirst]);

  const loadMore = useCallback(async () => {
    if (!cursor || !hasMore || loadingMore) return;
    try {
      setLoadingMore(true);
      const [page, hints, invoiceLinks, invoiceAmounts] = await Promise.all([
        AdminService.getSubscriptionsPage(100, cursor),
        AdminService.getLatestPaymentGatewayHintsByUserId(500),
        AdminService.getLatestInvoiceLinksByUserId(1000),
        AdminService.getLatestPaidInvoiceAmountsByUserId(1500)
      ]);
      setRows((prev) =>
        sortRows([
          ...prev,
          ...mergeInvoiceAmounts(
            mergeInvoiceActions(mergeSubscriptionPaymentHints(page.subscriptions, hints), invoiceLinks),
            invoiceAmounts
          )
        ])
      );
      setCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch (e) {
      console.error('AdminCurrentSubscriptionsPage loadMore', e);
      showError('Could not load the next page of subscriptions.');
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, hasMore, loadingMore, showError, sortRows]);

  const tableWrap = isDark ? 'border-neutral-700 bg-neutral-900/50' : 'border-slate-200 bg-white';

  const totalShown = useMemo(() => rows.length, [rows]);

  const handleGenerateInvoice = useCallback(async (row: AdminSubscription) => {
    try {
      setGeneratingInvoiceForUserId(row.userId);
      const adminStorageUid = currentUser?.uid;
      if (!adminStorageUid) {
        throw new Error('Admin session not ready. Please refresh and try again.');
      }
      const now = new Date();
      const periodStart = row.currentStart?.toDate ? row.currentStart.toDate() : now;
      const periodEnd = row.currentEnd?.toDate ? row.currentEnd.toDate() : now;
      const billingPeriod = row.interval === 'yearly' ? 'annual' : 'monthly';
      const invoiceId = invoiceService.generateInvoiceId(row.userId);
      const currencyLc = String((row.latestInvoiceCurrency || row.currency || 'usd') ?? 'usd').toLowerCase();
      const zeroDecimalMinorCurrencies = new Set(['jpy', 'krw', 'vnd']);
      const amountMinor = row.latestInvoiceAmountPaidMinor != null ? row.latestInvoiceAmountPaidMinor : row.amount || 0;
      const amount = zeroDecimalMinorCurrencies.has(currencyLc) ? amountMinor : amountMinor / 100;
      const paymentId = row.dodoPaymentId || row.lastPaymentReference || row.stripeSubscriptionId || row.dodoSubscriptionId;

      const result = await invoiceService.generateAndUploadInvoice({
        invoiceId,
        userId: row.userId,
        storageUserId: adminStorageUid,
        userName: row.displayName || row.companyName || row.userEmail || row.userId,
        userEmail: row.userEmail || '',
        planName: planTypeLabel(row.planId),
        amount,
        currency: currencyLc,
        billingPeriod,
        paymentDate:
          typeof row.latestInvoiceCreated === 'number' && row.latestInvoiceCreated > 0
            ? new Date(row.latestInvoiceCreated * 1000)
            : periodStart,
        periodStart,
        periodEnd,
        paymentId: paymentId || undefined,
        paymentMethodRaw: row.paymentMethod || undefined,
        subscribers: row.subscribersCap,
      });

      if (!result.success || !result.downloadUrl) {
        throw new Error(result.error || 'Failed to generate invoice PDF');
      }

      window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');
      setRows((prev) =>
        prev.map((r) =>
          r.userId === row.userId
            ? { ...r, latestInvoiceDownloadUrl: result.downloadUrl, latestInvoiceViewUrl: result.downloadUrl, latestInvoiceId: result.invoiceId || r.latestInvoiceId }
            : r
        )
      );
      showSuccess('Invoice generated and downloaded.');
    } catch (e) {
      console.error('handleGenerateInvoice:', e);
      showError(e instanceof Error ? e.message : 'Failed to generate invoice');
    } finally {
      setGeneratingInvoiceForUserId(null);
    }
  }, [currentUser?.uid, showError, showSuccess]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-cyan-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Current subscriptions</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Showing <strong>{totalShown}</strong> paid subscriber{totalShown === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={() => loadFirst()}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              isDark ? 'bg-neutral-800 text-white hover:bg-neutral-700' : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
            }`}
          >
            Refresh
          </button>
          {hasMore && (
            <button
              type="button"
              disabled={loadingMore}
              onClick={() => loadMore()}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium disabled:opacity-50 ${
                isDark ? 'bg-cyan-900/50 text-cyan-100 hover:bg-cyan-900' : 'bg-cyan-600 text-white hover:bg-cyan-700'
              }`}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      </div>

      <div className={`overflow-x-auto rounded-xl border shadow-sm ${tableWrap}`}>
        <table className="min-w-[1280px] w-full text-left text-sm">
          <thead>
            <tr className={`border-b ${isDark ? 'border-neutral-700 bg-neutral-900' : 'border-slate-200 bg-slate-50'}`}>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Email</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">User</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Company / name</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Plan type</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Billing</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Status</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Cap</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Period start</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Period end</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Est. MRR</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Provider</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Sub. id</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Since</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={14} className={`px-3 py-10 text-center ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                  No paid active subscriptions in the loaded pages yet.
                  {hasMore
                    ? ' Try Load more — the next Firestore page may include paid plans.'
                    : ' If you expect rows, confirm Firestore data and admin rules.'}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const extId = externalSubscriptionId(row);
                const emailsLine = formatEmailsLine(row.emailsPerMonth);
                return (
                  <tr
                    key={row.userId}
                    className={`border-b last:border-0 ${
                      isDark ? 'border-neutral-800 hover:bg-neutral-800/60' : 'border-slate-100 hover:bg-slate-50'
                    }`}
                  >
                    <td className="max-w-[220px] px-3 py-2">
                      <div className="truncate font-medium" title={row.userEmail}>
                        {row.userEmail || '—'}
                      </div>
                    </td>
                    <td className="max-w-[200px] px-3 py-2 font-mono text-xs">
                      <Link
                        to={`${ADMIN_BASE}/user-details/${row.userId}`}
                        className={`break-all underline-offset-2 hover:underline ${
                          isDark ? 'text-cyan-300' : 'text-cyan-700'
                        }`}
                        title={row.userId}
                      >
                        {truncateId(row.userId, 12)}
                      </Link>
                    </td>
                    <td className="max-w-[180px] px-3 py-2 text-xs">
                      <div className="truncate" title={row.companyName || ''}>
                        {row.companyName || '—'}
                      </div>
                      {row.displayName && (
                        <div className={`truncate ${isDark ? 'text-slate-500' : 'text-slate-500'}`} title={row.displayName}>
                          {row.displayName}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${planTypeBadgeClass(
                          row.planId,
                          isDark
                        )}`}
                      >
                        {planTypeLabel(row.planId)}
                      </span>
                      {row.planId && (
                        <div className={`mt-0.5 font-mono text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                          {String(row.planId).toLowerCase()}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">{row.interval === 'yearly' ? 'Annual' : 'Monthly'}</td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          row.status === 'active'
                            ? isDark
                              ? 'bg-emerald-900/40 text-emerald-100'
                              : 'bg-emerald-100 text-emerald-900'
                            : isDark
                              ? 'bg-amber-900/40 text-amber-100'
                              : 'bg-amber-100 text-amber-900'
                        }`}
                      >
                        {row.status}
                      </span>
                      {row.cancelAtPeriodEnd && (
                        <div className={`mt-0.5 text-[10px] ${isDark ? 'text-amber-200/90' : 'text-amber-800'}`}>
                          Cancels at period end
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">
                      {row.subscribersCap != null ? row.subscribersCap.toLocaleString() : '—'}
                      {emailsLine && (
                        <div className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{emailsLine}</div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs">{formatDate(row.currentStart)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs">{formatDate(row.currentEnd)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs font-medium">
                      {formatMoneyMinorUnits(row.amount, row.currency)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs capitalize">{providerLabel(row)}</td>
                    <td className="max-w-[140px] truncate px-3 py-2 font-mono text-[11px]" title={extId || undefined}>
                      {truncateId(extId, 16)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs">{formatDate(row.createdAt)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={generatingInvoiceForUserId === row.userId}
                          onClick={() => handleGenerateInvoice(row)}
                          className={`rounded-md px-2 py-1 font-medium ${
                            isDark
                              ? 'bg-cyan-900/40 text-cyan-100 hover:bg-cyan-900'
                              : 'bg-cyan-100 text-cyan-800 hover:bg-cyan-200'
                          } disabled:opacity-60`}
                        >
                          {generatingInvoiceForUserId === row.userId ? 'Generating…' : 'Generate Invoice'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && hasMore && (
        <p className={`mt-4 text-center text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
          This batch had no paid rows (e.g. many starter accounts). Use <strong>Load more</strong> to continue.
        </p>
      )}
    </div>
  );
};

export default AdminCurrentSubscriptionsPage;
